import { PatientAgeOperator, ScopeMode } from '@billing/validation/rules';
import {
  Group,
  MultiSelect,
  NumberInput,
  Select,
  TextInput,
  Textarea,
  Text,
} from '@mantine/core';
import type { ReactElement } from 'react';
import {
  categoricalDemographicScopeModes,
  tariffScopeModes,
} from './constants';
import {
  getWhenComparisonValue,
  getWhenComparisonUpdate,
  parseValueList,
  getClaimCriteriaField,
  isWhenRangeOperator,
} from './ruleUtils';
import { RuleClaimValueSelect } from './RuleClaimValueSelect';
import { getRuleClaimFieldOptions } from './ruleClaimFieldOptions';
import type { RuleCriteriaClauseProps } from './RuleCriteriaRow';

/** Keeps partially entered ranges intact until both endpoints are valid. */
const RuleTypedCriteriaValues = ({
  clause,
  index,
  builder,
}: RuleCriteriaClauseProps): ReactElement | null => {
  if (clause.whenOperator === 'exists' || clause.whenOperator === 'notExists')
    return null;
  const { translate, updateWhenClause } = builder;
  const input = getClaimCriteriaField(clause.scopeMode)?.input;
  if (input === 'text')
    return (
      <Textarea
        placeholder={translate('pages:rules.criteriaFields.value')}
        value={clause.scopeValuesText}
        onChange={(event) =>
          updateWhenClause(clause.id, {
            scopeValuesText: event.currentTarget.value,
          })
        }
        style={{ flex: 1 }}
        data-testid={`rule-claim-values-${index}`}
      />
    );
  const range = isWhenRangeOperator(clause.whenOperator);
  const date = input === 'date';
  const values = clause.scopeValuesText.split(',');
  const updateValue = (value: string, position: number): void => {
    if (!range) {
      updateWhenClause(clause.id, { scopeValuesText: value });
      return;
    }
    const next = [values[0] ?? '', values[1] ?? ''];
    next[position] = value;
    updateWhenClause(clause.id, { scopeValuesText: next.join(',') });
  };
  const valueFields = (range ? [0, 1] : [0]).map((position) => {
    const label = translate('pages:rules.criteriaFields.value');
    const rangeLabel = translate(
      `pages:rules.criteriaFields.${position === 0 ? 'start' : 'end'}`
    );
    const testID = `rule-claim-values-${index}${range ? `-${position}` : ''}`;
    if (date)
      return (
        <TextInput
          key={position}
          type="date"
          label={range ? rangeLabel : label}
          description={`${translate(
            'pages:rules.criteriaFields.siteDateTimezone'
          )} (${builder.timezone})`}
          value={values[position] ?? ''}
          onChange={(event) => updateValue(event.currentTarget.value, position)}
          data-testid={testID}
        />
      );
    return (
      <NumberInput
        key={position}
        label={range ? rangeLabel : label}
        min={0}
        allowDecimal={false}
        allowNegative={false}
        trimLeadingZeroesOnBlur={false}
        value={values[position] ?? ''}
        onChange={(value) => updateValue(String(value), position)}
        data-testid={testID}
      />
    );
  });

  return (
    <Group grow style={{ flex: 1 }}>
      {valueFields}
    </Group>
  );
};

const RulePatientAgeCriteriaFields = ({
  builder,
  clause,
  index,
}: RuleCriteriaClauseProps): ReactElement => {
  const {
    translate,
    updateWhenClause,
    localizedPatientAgeOperatorOptions,
    localizedWhenOperatorOptions,
  } = builder;
  const supportsValueComparison =
    clause.whenOperator === 'is' || clause.whenOperator === 'isNot';
  const ageFields =
    clause.patientAgeOperator === PatientAgeOperator.BetweenInclusive ? (
      <>
        <NumberInput
          label={translate('pages:rules.createModal.fields.minimumAgeLabel')}
          min={0}
          allowDecimal={false}
          allowNegative={false}
          value={clause.patientAgeStart}
          onChange={(value) =>
            updateWhenClause(clause.id, {
              patientAgeStart: String(value),
            })
          }
          data-testid={`rule-patient-age-start-${index}`}
        />
        <NumberInput
          label={translate('pages:rules.createModal.fields.maximumAgeLabel')}
          min={0}
          allowDecimal={false}
          allowNegative={false}
          value={clause.patientAgeEnd}
          onChange={(value) =>
            updateWhenClause(clause.id, {
              patientAgeEnd: String(value),
            })
          }
          data-testid={`rule-patient-age-end-${index}`}
        />
      </>
    ) : (
      <NumberInput
        label={translate('pages:rules.createModal.fields.ageValueLabel')}
        min={0}
        allowDecimal={false}
        allowNegative={false}
        value={clause.patientAgeValue}
        onChange={(value) =>
          updateWhenClause(clause.id, {
            patientAgeValue: String(value),
          })
        }
        data-testid={`rule-patient-age-value-${index}`}
      />
    );
  const visibleAgeFields = supportsValueComparison && ageFields;

  return (
    <Group grow align="flex-end" gap="sm" flex={1} miw={280}>
      <Select
        label={translate('pages:rules.createModal.fields.ageOperatorLabel')}
        data={[
          ...localizedPatientAgeOperatorOptions,
          {
            value: 'notEqual',
            label: translate('pages:rules.criteriaComparisons.notEqual'),
          },
          {
            value: 'notBetween',
            label: translate('pages:rules.criteriaComparisons.notBetween'),
          },
          ...localizedWhenOperatorOptions.filter(
            (option) =>
              option.value === 'exists' || option.value === 'notExists'
          ),
        ]}
        value={getWhenComparisonValue(clause)}
        onChange={(value) =>
          updateWhenClause(clause.id, {
            ...getWhenComparisonUpdate(
              clause.scopeMode,
              value || PatientAgeOperator.Equal
            ),
          })
        }
        allowDeselect={false}
        data-testid={`rule-patient-age-operator-${index}`}
      />
      {visibleAgeFields}
    </Group>
  );
};

/** Chooses the value editor while keeping every row connected to the same builder state. */
export const RuleCriteriaValueFields = ({
  builder,
  clause,
  index,
}: RuleCriteriaClauseProps): ReactElement => {
  const { translate, updateWhenClause, getDemographicOptions } = builder;
  const claimField = getClaimCriteriaField(clause.scopeMode);
  const selectOptions = getRuleClaimFieldOptions(clause.scopeMode, translate);
  if (clause.scopeMode === ScopeMode.DuplicateClaim) {
    return (
      <Text size="sm" c="dimmed">
        {translate('pages:rules.historyCriteria.duplicateDescription')}
      </Text>
    );
  }
  if (clause.scopeMode === ScopeMode.TariffBillingHistory) {
    return (
      <TextInput
        aria-label={translate('pages:rules.historyCriteria.tariffCodes')}
        placeholder={translate('pages:rules.historyCriteria.tariffPlaceholder')}
        value={clause.scopeValuesText}
        onChange={(event) =>
          updateWhenClause(clause.id, {
            scopeValuesText: event.currentTarget.value,
          })
        }
        data-testid={`rule-billing-history-tariffs-${index}`}
      />
    );
  }
  if (clause.scopeMode === ScopeMode.PatientAge)
    return (
      <RulePatientAgeCriteriaFields
        builder={builder}
        clause={clause}
        index={index}
      />
    );
  const supportsValueComparison =
    clause.whenOperator === 'is' || clause.whenOperator === 'isNot';
  const tariffValuesField =
    tariffScopeModes.has(clause.scopeMode) &&
    clause.scopeMode !== ScopeMode.SpecialtyCode &&
    supportsValueComparison ? (
      <TextInput
        placeholder={translate('pages:rules.editControls.value')}
        value={clause.scopeValuesText}
        onChange={(event) =>
          updateWhenClause(clause.id, {
            scopeValuesText: event.currentTarget.value,
          })
        }
        style={{ flex: 1 }}
        data-testid={
          index === 0
            ? 'rule-scope-values-input'
            : `rule-scope-values-input-${index}`
        }
      />
    ) : null;
  const claimSelectField =
    selectOptions && supportsValueComparison ? (
      <RuleClaimValueSelect
        key={clause.scopeMode}
        multiple
        options={selectOptions}
        value={parseValueList(clause.scopeValuesText)}
        onChange={(values) =>
          updateWhenClause(clause.id, {
            scopeValuesText: values.join(','),
          })
        }
        placeholder={translate(
          'pages:rules.createModal.fields.demographicValuesPlaceholder'
        )}
        testID={`rule-claim-values-${index}`}
      />
    ) : null;
  const identifierValuesField =
    claimField?.input === 'identifier' && supportsValueComparison ? (
      <TextInput
        placeholder={translate(
          clause.scopeMode === ScopeMode.ReferringProvider
            ? 'pages:rules.createModal.fields.referringProviderValuesPlaceholder'
            : 'pages:rules.editControls.value'
        )}
        value={clause.scopeValuesText}
        onChange={(event) =>
          updateWhenClause(clause.id, {
            scopeValuesText: event.currentTarget.value,
          })
        }
        style={{ flex: 1 }}
        data-testid={`rule-claim-values-${index}`}
      />
    ) : null;
  const typedValuesField = (claimField?.input === 'number' ||
    claimField?.input === 'text' ||
    claimField?.input === 'date') && (
    <RuleTypedCriteriaValues clause={clause} index={index} builder={builder} />
  );
  const demographicValuesField =
    categoricalDemographicScopeModes.has(clause.scopeMode) &&
    supportsValueComparison ? (
      <MultiSelect
        data={getDemographicOptions(clause.scopeMode)}
        value={parseValueList(clause.scopeValuesText)}
        onChange={(values) =>
          updateWhenClause(clause.id, {
            scopeValuesText: values.join(','),
          })
        }
        placeholder={translate(
          'pages:rules.createModal.fields.demographicValuesPlaceholder'
        )}
        searchable
        clearable
        style={{ flex: 1 }}
        data-testid={`rule-demographic-values-${index}`}
      />
    ) : null;

  return (
    <>
      {tariffValuesField}
      {claimSelectField}
      {identifierValuesField}
      {typedValuesField}
      {demographicValuesField}
    </>
  );
};
